import React from 'react';

import {usePersistentViewState} from '../lib/usePersistentViewState.js';

// ============================================================================
// Collapsible — a section that folds (Design Constitution L12, L14, L21, §2)
// ----------------------------------------------------------------------------
// The header is one button: chevron on the left, the Title Case label, a gray
// count, and any trailing summary (a group total, L14). It pops out like any
// clickable (§2) and carries aria-expanded / aria-controls. Neutral header:
// no pastel band (L14).
// State is remembered for the session when stateKey is given (the same
// session store list views already use). Archived and complete groups start
// closed: the caller passes defaultOpen={false} (L12, C14).
// The open/close state can also be controlled (open + onOpenChange) when a
// page owns it.
// ============================================================================

type CollapsibleProps = {
  label: React.ReactNode;
  count?: number | string | null;
  summary?: React.ReactNode;
  defaultOpen?: boolean;
  stateKey?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  headingLevel?: 2 | 3 | 4;
  id?: string;
  // Data hooks a screen's tests already select by (data-*), kept when the
  // screen moves onto this fold.
  toggleAttrs?: Record<string, string>;
  children: React.ReactNode;
};

function useOpenState(stateKey: string | undefined, defaultOpen: boolean): [boolean, (next: boolean) => void] {
  // Both hooks always run (hook order); only one is the source of truth.
  const [stored, setStored] = usePersistentViewState<boolean>(`collapsible:${stateKey || '_'}`, defaultOpen);
  const [local, setLocal] = React.useState(defaultOpen);
  return stateKey ? [stored, setStored] : [local, setLocal];
}

export default function Collapsible({
  label,
  count,
  summary,
  defaultOpen = true,
  stateKey,
  open: controlledOpen,
  onOpenChange,
  headingLevel = 3,
  id,
  toggleAttrs,
  children,
}: CollapsibleProps) {
  const [ownOpen, setOwnOpen] = useOpenState(stateKey, defaultOpen);
  const open = controlledOpen ?? ownOpen;
  const autoId = React.useId().replace(/:/g, '');
  const regionId = `${id || `collapsible-${autoId}`}-region`;
  const Heading = `h${headingLevel}` as 'h2' | 'h3' | 'h4';
  const toggle = () => {
    const next = !open;
    if (controlledOpen === undefined) setOwnOpen(next);
    onOpenChange?.(next);
  };
  return (
    <section id={id} data-collapsible={open ? 'open' : 'closed'} style={{marginBottom: 12}}>
      <Heading style={{margin: 0, fontSize: 'inherit', fontWeight: 'inherit'}}>
        <button
          {...toggleAttrs}
          type="button"
          className="wcf-clickable"
          aria-expanded={open}
          aria-controls={regionId}
          data-collapsible-toggle="1"
          onClick={toggle}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            width: '100%',
            minHeight: 44,
            padding: '10px 14px',
            border: '1px solid var(--border)',
            borderRadius: 12,
            background: 'var(--surface)',
            color: 'var(--ink)',
            fontFamily: 'inherit',
            fontSize: 15,
            fontWeight: 700,
            textAlign: 'left',
            cursor: 'pointer',
          }}
        >
          <span
            aria-hidden="true"
            data-collapsible-chevron="1"
            style={{
              display: 'inline-block',
              width: 14,
              color: 'var(--text-2)',
              transform: open ? 'rotate(90deg)' : 'none',
              transition: 'transform var(--motion)',
            }}
          >
            ›
          </span>
          <span>{label}</span>
          {count != null && count !== '' && (
            <span data-collapsible-count="1" style={{color: 'var(--label)', fontWeight: 600}}>
              {count}
            </span>
          )}
          {summary != null && (
            <span style={{marginLeft: 'auto', color: 'var(--text-2)', fontWeight: 600, fontSize: 14}}>{summary}</span>
          )}
        </button>
      </Heading>
      <div id={regionId} hidden={!open} data-collapsible-region="1" style={{paddingTop: 10}}>
        {open ? children : null}
      </div>
    </section>
  );
}
